import { useCallback, useMemo, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import type { DatePickerParams } from 'fe-be-utils';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { cn } from '../../lib/utils';
import { getBackgroundStyle } from '../../utils/theme';

type DatePickerComponentProps = {
  params: DatePickerParams;
  onSubmit: (value: string) => void;
  onDismiss: () => void;
  accentColor?: string;
  textOverAccentColor?: string;
};

const formatIso = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseIsoDate = (iso?: string): Date | undefined => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const daysInMonth = (month: number, year: number) => new Date(year, month, 0).getDate();

type Segments = { mm: string; dd: string; yyyy: string };

const segmentsToDate = (s: Segments): Date | undefined => {
  const m = parseInt(s.mm, 10);
  const d = parseInt(s.dd, 10);
  const y = parseInt(s.yyyy, 10);
  if (!m || !d || !y || s.yyyy.length < 4) return undefined;
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(m, y)) return undefined;
  return new Date(y, m - 1, d);
};

const dateToSegments = (date: Date): Segments => ({
  mm: String(date.getMonth() + 1).padStart(2, '0'),
  dd: String(date.getDate()).padStart(2, '0'),
  yyyy: String(date.getFullYear())
});

export const DatePickerComponent = ({
  params,
  onSubmit,
  onDismiss,
  accentColor,
  textOverAccentColor
}: DatePickerComponentProps) => {
  const [segments, setSegments] = useState<Segments>({ mm: '', dd: '', yyyy: '' });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const mmRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLInputElement>(null);
  const yyyyRef = useRef<HTMLInputElement>(null);

  const minDate = useMemo(() => parseIsoDate(params.minDate), [params.minDate]);
  const maxDate = useMemo(() => parseIsoDate(params.maxDate), [params.maxDate]);

  const selected = useMemo(() => segmentsToDate(segments), [segments]);

  const isWithinBounds = useMemo(() => {
    if (!selected) return false;
    if (minDate && selected < minDate) return false;
    if (maxDate && selected > maxDate) return false;
    return true;
  }, [selected, minDate, maxDate]);

  const updateSegment = useCallback(
    (
      field: keyof Segments,
      value: string,
      maxLen: number,
      nextRef?: React.RefObject<HTMLInputElement | null>
    ) => {
      const digits = value.replace(/\D/g, '').slice(0, maxLen);
      setSegments((prev) => ({ ...prev, [field]: digits }));
      if (digits.length === maxLen && nextRef?.current) {
        nextRef.current.focus();
        nextRef.current.select();
      }
    },
    []
  );

  const handleKeyDown = useCallback(
    (
      field: keyof Segments,
      e: React.KeyboardEvent<HTMLInputElement>,
      prevRef?: React.RefObject<HTMLInputElement | null>
    ) => {
      if (e.key === 'Backspace' && !segments[field] && prevRef?.current) {
        e.preventDefault();
        prevRef.current.focus();
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const delta = e.key === 'ArrowUp' ? 1 : -1;
        setSegments((prev) => {
          const val = parseInt(prev[field], 10) || 0;
          if (field === 'mm') return { ...prev, mm: String(clamp(val + delta, 1, 12)).padStart(2, '0') };
          if (field === 'dd') {
            const m = parseInt(prev.mm, 10) || 1;
            const y = parseInt(prev.yyyy, 10) || new Date().getFullYear();
            return { ...prev, dd: String(clamp(val + delta, 1, daysInMonth(m, y))).padStart(2, '0') };
          }
          const newYear = clamp(val + delta, 1900, 2100);
          return { ...prev, yyyy: String(newYear) };
        });
      }
    },
    [segments]
  );

  const handleCalendarSelect = (date: Date) => {
    setSegments(dateToSegments(date));
    setCalendarOpen(false);
  };

  return (
    <div className="mb-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-3 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{params.prompt}</p>

      <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
        <input
          ref={mmRef}
          type="text"
          inputMode="numeric"
          placeholder="MM"
          value={segments.mm}
          onChange={(e) => updateSegment('mm', e.target.value, 2, ddRef)}
          onKeyDown={(e) => handleKeyDown('mm', e)}
          onFocus={(e) => e.target.select()}
          className={cn(
            'w-7 bg-transparent text-center text-sm outline-none',
            'text-gray-900 placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500'
          )}
        />
        <span className="text-sm text-gray-400 dark:text-gray-500">/</span>
        <input
          ref={ddRef}
          type="text"
          inputMode="numeric"
          placeholder="DD"
          value={segments.dd}
          onChange={(e) => updateSegment('dd', e.target.value, 2, yyyyRef)}
          onKeyDown={(e) => handleKeyDown('dd', e, mmRef)}
          onFocus={(e) => e.target.select()}
          className={cn(
            'w-7 bg-transparent text-center text-sm outline-none',
            'text-gray-900 placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500'
          )}
        />
        <span className="text-sm text-gray-400 dark:text-gray-500">/</span>
        <input
          ref={yyyyRef}
          type="text"
          inputMode="numeric"
          placeholder="YYYY"
          value={segments.yyyy}
          onChange={(e) => updateSegment('yyyy', e.target.value, 4)}
          onKeyDown={(e) => handleKeyDown('yyyy', e, ddRef)}
          onFocus={(e) => e.target.select()}
          className={cn(
            'w-11 bg-transparent text-center text-sm outline-none',
            'text-gray-900 placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500'
          )}
        />
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setCalendarOpen(!calendarOpen)}
          className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        >
          <CalendarDays className="h-4 w-4" />
        </button>
      </div>

      {calendarOpen && (
        <div className="mt-2">
          <Calendar
            selected={selected}
            onSelect={handleCalendarSelect}
            minDate={minDate}
            maxDate={maxDate}
            accentColor={accentColor}
            textOverAccentColor={textOverAccentColor}
          />
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          onClick={() => isWithinBounds && selected && onSubmit(formatIso(selected))}
          disabled={!isWithinBounds}
          style={
            accentColor
              ? { ...getBackgroundStyle(accentColor), color: textOverAccentColor ?? '#ffffff' }
              : undefined
          }
        >
          Confirm
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Skip
        </Button>
      </div>
    </div>
  );
};
